import { useState, useEffect } from 'react';
import { Box, Grid, Typography, IconButton } from '@mui/material';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import axios from 'axios';
import { getThisYearCourses } from '../services/getThisYearCourses';
import { FestivosDescriptions } from './FestivosDescriptions';  // Importa el componente
import { useTranslation } from 'react-i18next'; // Importar hook de traducción

import { differenceInDays } from 'date-fns';

export const CustomCalendar = ({ attendanceData }) => {
  const { t } = useTranslation(); // Inicializar hook para usar las traducciones
  const [currentDate, setCurrentDate] = useState(new Date(Date.parse(attendanceData[0].fecha)));

  // Convertir los strings de las traducciones en arrays
  const daysInWeek = t('calendar.daysOfWeek').split(',');
  const monthNames = t('calendar.months').split(',');

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: days }, (_, i) => new Date(year, month, i + 1));
  };

  const days = getDaysInMonth(currentDate);
  const startingDayIndex = days[0].getDay();

  const getAttendanceColor = (day) => {
    const matchingAttendance = attendanceData.find(a => new Date(a.fecha).setHours(0, 0, 0, 0) === day.setHours(0, 0, 0, 0));
    if (matchingAttendance) {
      switch (matchingAttendance.valor) {
        case 1: return '#50a579'; // Asistencia
        case 2: return '#e28b48'; // Demora
        case 3: return '#df7868'; // Falta
        case 4: return '#507cba'; // Asistencia Online
        default: return 'transparent';
      }
    }
    return 'transparent';
  };

  const handlePreviousMonth = () => {
    setCurrentDate(prevDate => new Date(prevDate.getFullYear(), prevDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prevDate => new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 1));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={handlePreviousMonth}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <Typography variant="h6">
          {`${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>
      <Grid container spacing={1}>
        {daysInWeek.map((day, index) => (
          <Grid item xs={1.7} key={index}>
            <Typography variant="body2" align="center">{day}</Typography>
          </Grid>
        ))}
        {Array.from({ length: startingDayIndex }).map((_, index) => (
          <Grid item xs={1.7} key={`empty-${index}`} />
        ))}
        {days.map((day) => (
          <Grid item xs={1.7} key={day.toISOString()}>
            <Box
              sx={{
                width: '100%',
                aspectRatio: '1/1',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                border: '1px solid',
                borderRadius: '50%',
                borderColor: getAttendanceColor(day),
                backgroundColor: getAttendanceColor(day),
                color: getAttendanceColor(day) !== 'transparent' ? 'white' : 'inherit'
              }}
            >
              {day.getDate()}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};


// Calendario para la página de calendario
export const ActualYearCalendar = ({ }) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [festivosData, setFestivosData] = useState([]);
  const [calendarios, setCalendarios] = useState([]);
  const [festivosDescriptions, setFestivosDescriptions] = useState([]);
  const [activeTipos, setActiveTipos] = useState([
    { nombre: 'clase', color: '#50A579', text: t('calendar.class') }
  ]);

  const daysInWeek = t('calendar.daysOfWeek').split(',');
  const monthNames = t('calendar.months').split(',');

  useEffect(() => {
    const fetchCalendarData = async () => {
      try {
        const thisYearCourses = await getThisYearCourses();
        setCalendarios(thisYearCourses);
      } catch (error) {
        console.error('Error al obtener el calendario del curso:', error);
      }
    };

    const fetchFestivos = async () => {
      try {
        const response = await axios.get(`${import.meta.env.VITE_APP_API_URL}festivos`, {
          params: { date: currentDate }
        });
        
        setFestivosData(response.data);
        
        const tiposSet = new Set();
        const tiposList = [{ nombre: 'clase', color: '#50A579', text: t('calendar.class') }];
        
        response.data.forEach(festivo => {
          const tipoNombre = festivo.tipo || 'festivo';
          
          if (!tiposSet.has(tipoNombre)) {
            tiposSet.add(tipoNombre);
            
            const color = festivo.tipo_fecha?.color || 
                         (tipoNombre === 'festivo' ? '#F44336' : '#FFC107');
            
            const text = t(`calendar.${tipoNombre}`, { 
              defaultValue: tipoNombre.charAt(0).toUpperCase() + tipoNombre.slice(1) 
            });
            
            tiposList.push({ nombre: tipoNombre, color, text });
          }
        });
        
        setActiveTipos(tiposList);
        
        const descriptions = response.data.map(festivo => ({
          text: festivo.descripcion,
          date: String(new Date(festivo.fecha)),
          tipo: festivo.tipo || 'festivo',
          color: festivo.tipo_fecha?.color || 
                 (festivo.tipo === 'festivo' ? '#F44336' : '#FFC107')
        }));

        // Agrupar descripciones por tipo y fecha consecutiva
        const groupedDescriptions = descriptions.reduce((acc, curr) => {
          const last = acc[acc.length - 1];
          const currDate = new Date(curr.date);
          const splittedDate = last?.date.split("-");
          const lastDate = last ? new Date(splittedDate[splittedDate.length - 1]) : null;
          
          if (last && last.text === curr.text && last.tipo === curr.tipo && differenceInDays(currDate, lastDate) <= 1) {
            last.date = `${new Date(splittedDate[0])} - ${curr.date}`;
          } else {
            acc.push(curr);
          }
        
          return acc;
        }, []);

        const formattedDescriptions = groupedDescriptions.map(description => ({
          ...description,
          date: description.date.split("-").map(date => new Date(date).toLocaleDateString()).join(" - ")
        }));

        setFestivosDescriptions(formattedDescriptions);

      } catch (error) {
        console.error('Error al obtener los festivos:', error);
      }
    };

    fetchCalendarData();
    fetchFestivos();
  }, [currentDate, t]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: days }, (_, i) => new Date(year, month, i + 1, 2));
  };

  const isDayInRange = (day, calendario) => {
    const startDate = new Date(calendario.fecini);
    const endDate = new Date(calendario.fecfin);
    return day >= startDate && day <= endDate;
  };

  const isClassDay = (day, calendario) => {
    if (!isDayInRange(day, calendario)) return false
    const dayOfWeek = day.getDay();

    switch (dayOfWeek) {
      case 1: return calendario.chklunes;    // Lunes
      case 2: return calendario.chkmartes;   // Martes
      case 3: return calendario.chkmiercoles; // Miércoles
      case 4: return calendario.chkjueves;   // Jueves
      case 5: return calendario.chkviernes;  // Viernes
      case 6: return calendario.chksábado;   // Sábado
      case 0: return calendario.chkdomingo;  // Domingo
      default: return false;
    }
  }

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDayColor = (day) => {
    const formattedDate = formatDate(day);
    const matchingFestivo = festivosData.find(f => f.fecha === formattedDate);

    if (matchingFestivo) {
      // Buscar primero el color en tipo_fecha de la respuesta de la API
      if (matchingFestivo.tipo_fecha?.color) {
        return matchingFestivo.tipo_fecha.color;
      }
      // Si no está en la respuesta, buscar en nuestros tipos activos
      const activoTipo = activeTipos.find(t => t.nombre === (matchingFestivo.tipo || 'festivo'));
      if (activoTipo) {
        return activoTipo.color;
      }
      return '#F44336'; // Color por defecto para festivos
    }
    
    if (calendarios?.some(calendario => isClassDay(day, calendario))) {
      return '#50A579'; // Color para días con clase
    }
    
    return 'transparent';
  };

  const handlePreviousMonth = () => {
    setCurrentDate(prevDate => new Date(prevDate.getFullYear(), prevDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prevDate => new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 1));
  };

  const days = getDaysInMonth(currentDate);
  const startingDayIndex = days[0].getDay();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={handlePreviousMonth}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <Typography variant="h6">
          {`${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>
      <Grid container spacing={1}>
        {daysInWeek.map(day => (
          <Grid item xs={1.7} key={day}>
            <Typography variant="body2" align="center">{day}</Typography>
          </Grid>
        ))}
        {Array.from({ length: startingDayIndex }).map((_, index) => (
          <Grid item xs={1.7} key={`empty-${index}`} />
        ))}
        {days.map((day) => (
          <Grid item xs={1.7} key={day.toISOString()}>
            <Box
              sx={{
                width: '100%',
                aspectRatio: '1/1',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                border: '1px solid',
                borderRadius: '50%',
                borderColor: getDayColor(day),
                backgroundColor: getDayColor(day),
                color: getDayColor(day) !== 'transparent' ? 'white' : 'inherit'
              }}
            >
              {day.getDate()}
            </Box>
          </Grid>
        ))}
      </Grid>
      <FestivosDescriptions festivosDescriptions={festivosDescriptions} />
    </Box>
  );
};